import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { ZAR } from "@/lib/cart";
import { mediaUrl } from "@/lib/api";

/**
 * Horizontal snap-scroll carousel for a compact product strip.
 * - Native touch/wheel scroll on mobile (no libs).
 * - Left/right nav buttons on desktop.
 * - Uses a slimmer product card so more items fit per screen.
 */
export default function ProductCarousel({ title, kicker, products = [], accent = "#00FFC2" }) {
  const scrollerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      setAtStart(el.scrollLeft <= 4);
      setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [products.length]);

  const nudge = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = Math.max(240, Math.floor(el.clientWidth * 0.9));
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!products?.length) return null;

  return (
    <section
      data-testid={`carousel-${(title || "").toLowerCase().replace(/\s+/g, "-")}`}
      className="mt-14 sm:mt-20"
    >
      <div className="flex items-end justify-between mb-4 sm:mb-6">
        <div>
          <div className="font-label text-[10px] sm:text-xs" style={{ color: accent }}>
            / {kicker}
          </div>
          <h2 className="mt-1 sm:mt-2 font-display text-2xl font-black tracking-tighter sm:text-3xl">
            {title}
          </h2>
        </div>
        <div className="hidden md:flex gap-2">
          <button
            aria-label="Scroll left"
            onClick={() => nudge(-1)}
            disabled={atStart}
            data-testid="carousel-prev"
            className="inline-flex h-9 w-9 items-center justify-center border border-white/15 hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <button
            aria-label="Scroll right"
            onClick={() => nudge(1)}
            disabled={atEnd}
            data-testid="carousel-next"
            className="inline-flex h-9 w-9 items-center justify-center border border-white/15 hover:border-white/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <CaretRight size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="relative">
        {/* Left / right fade edges */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-black to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-black to-transparent z-10" />

        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-4 sm:mx-0 px-4 sm:px-0"
          style={{ scrollbarWidth: "thin" }}
        >
          {products.map((p, i) => (
            <CarouselCard key={p.slug} product={p} index={i} accent={accent} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CarouselCard({ product, index, accent }) {
  const img = product.images?.[0];
  const onSale = product.compare_at && product.compare_at > product.price;
  const salePct = onSale
    ? Math.round((1 - product.price / product.compare_at) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: (index % 8) * 0.03 }}
      className="snap-start shrink-0 w-[150px] sm:w-[180px] md:w-[200px]"
    >
      <Link
        to={`/product/${product.slug}`}
        className="group block border border-white/10 bg-[#0d0d0d] hover:border-white/25 transition-colors"
      >
        <div className="relative aspect-square overflow-hidden bg-gradient-to-b from-black to-[#141414]">
          {img ? (
            <img
              src={mediaUrl(img)}
              alt={product.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-white/20 text-[10px]">no image</div>
          )}
          {onSale && (
            <span
              className="absolute left-1.5 top-1.5 px-1.5 py-[1px] font-label text-[7px] font-black tracking-wider leading-tight"
              style={{ background: "#FFC900", color: "#000" }}
            >
              −{salePct}%
            </span>
          )}
          {product.featured && (
            <span
              className="absolute right-1.5 top-1.5 px-1.5 py-[1px] font-label text-[7px] font-black tracking-wider leading-tight"
              style={{ background: "#FF5A1F", color: "#000" }}
            >
              FEATURED
            </span>
          )}
        </div>
        <div className="p-2.5">
          <div className="font-label text-[8px] text-white/40 truncate">{product.brand}</div>
          <div className="mt-0.5 font-display text-[13px] font-bold text-white line-clamp-1">
            {product.name}
          </div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <span className="font-display text-sm font-black" style={{ color: accent }}>
              {ZAR(product.price)}
            </span>
            {onSale && (
              <span className="text-[9px] text-white/40 line-through">{ZAR(product.compare_at)}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
